import React from 'react';
import PropTypes from 'prop-types';

/**
 * 
 * @param {number} percent - درصد پیشرفت (0-100)
 * @param {string} className - کلاس‌های اضافی برای استایل
 * @param {boolean} showBar - نمایش نوار پیشرفت
 * @param {boolean} showText - نمایش متن درصد
 * @param {string} size - اندازه badge (sm, md, lg)
 * @param {string} variant - نوع نمایش (bar, circle, badge)
 */
const ProgressBadge = ({
  percent,
  className = '',
  showBar = true,
  showText = true,
  size = 'md',
  variant = 'bar',
}) => {
  if (percent === null || percent === undefined || percent === '') return null;

  const value = Math.min(Math.max(Number(percent), 0), 100);

  const getProgressColor = (val) => {
    if (val < 30) {
      return {
        bar: 'bg-red-500 dark:bg-red-600',
        text: 'text-red-700 dark:text-red-400',
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
      };
    }
    if (val < 70) {
      return {
        bar: 'bg-amber-500 dark:bg-amber-600',
        text: 'text-amber-700 dark:text-amber-400',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
      };
    }
    return {
      bar: 'bg-emerald-500 dark:bg-emerald-600',
      text: 'text-emerald-700 dark:text-emerald-400',
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-200 dark:border-emerald-800',
    };
  };

  // تعریف اندازه‌ها
  const sizeClasses = {
    sm: {
      bar: 'h-1',
      text: 'text-xs',
      container: 'gap-1.5',
      badge: 'px-2 py-0.5 text-xs',
    },
    md: {
      bar: 'h-2',
      text: 'text-xs',
      container: 'gap-2',
      badge: 'px-2.5 py-1 text-xs',
    },
    lg: {
      bar: 'h-2.5',
      text: 'text-sm',
      container: 'gap-2.5',
      badge: 'px-3 py-1.5 text-sm',
    },
  };

  const color = getProgressColor(value);
  const sizeConfig = sizeClasses[size] || sizeClasses.md;

  // نوع نمایش: bar (نوار + متن)
  if (variant === 'bar') {
    return (
      <div className={`flex items-center ${sizeConfig.container} ${className}`}>
        {showBar && (
          <div className="relative flex-1 min-w-[80px] max-w-[120px] rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${color.bar} rounded-full transition-all duration-500 ease-out`}
              style={{ width: `${value}%` }}
            />
          </div>
        )}
        {showText && (
          <span
            className={`font-semibold ${color.text} ${sizeConfig.text} min-w-[36px] text-left whitespace-nowrap`}
            style={{ fontFamily: 'CustomFont' }}
          >
            {value}٪
          </span>
        )}
      </div>
    );
  }

  // نوع نمایش: badge (فقط متن در badge)
  if (variant === 'badge') {
    return (
      <span
        className={`
          inline-flex items-center font-medium rounded-full
          ${color.bg} ${color.text} ${color.border}
          border ${sizeConfig.badge}
          transition-colors duration-200
          ${className}
        `}
        style={{ fontFamily: 'CustomFont' }}
      >
        {value}٪
      </span>
    );
  }

  return (
    <div className={`flex items-center ${sizeConfig.container} ${className}`}>
      {showBar && (
        <div className="relative w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <svg className="transform -rotate-90 w-12 h-12">
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              className="text-gray-200 dark:text-gray-700"
            />
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 20}`}
              strokeDashoffset={`${2 * Math.PI * 20 * (1 - value / 100)}`}
              className={color.bar}
              style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
            />
          </svg>
          {showText && (
            <span
              className={`absolute font-semibold ${color.text} ${sizeConfig.text}`}
              style={{ fontFamily: 'CustomFont' }}
            >
              {value}٪
            </span>
          )}
        </div>
      )}
      {!showBar && showText && (
        <span
          className={`font-semibold ${color.text} ${sizeConfig.text}`}
          style={{ fontFamily: 'CustomFont' }}
        >
          {value}٪
        </span>
      )}
    </div>
  );
};

ProgressBadge.propTypes = {
  percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  showBar: PropTypes.bool,
  showText: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['bar', 'circle', 'badge']),
};

export default ProgressBadge;
